import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Send, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudioContact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    service_type: "production",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setFormData({
        name: "",
        email: "",
        company: "",
        service_type: "production",
        message: ""
      });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
      console.error("Error submitting form:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] studio-body">
      <SEO brand="studio" path="/studio/contact" title="Contact" description="Ready to roll camera? Get in touch with Wake N' Bake Studio." />
      <Navigation brand="studio" />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 film-grain">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="heading-font text-5xl md:text-7xl text-white mb-6" data-testid="studio-contact-heading">
              LET'S CREATE
              <span className="text-[#F97316]"> TOGETHER</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Tell us about your project and let's create something cinematic.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h2 className="heading-font text-3xl text-white mb-8">GET IN TOUCH</h2>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <Mail className="w-6 h-6 text-[#F97316] mt-1" />
                  <div>
                    <div className="text-white font-medium mb-1">Email</div>
                    <a href="mailto:studio@wakenbake.com" className="text-gray-400 hover:text-[#F97316] transition-colors">
                      studio@wakenbake.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-[#F97316] mt-1" />
                  <div>
                    <div className="text-white font-medium mb-1">Location</div>
                    <div className="text-gray-400">Worldwide Production</div>
                  </div>
                </div>
              </div>

              <div className="bg-[#0A0A0A] border-2 border-[#F97316]/30 p-8 rounded-none">
                <h3 className="text-xl font-semibold text-white mb-4">What to Expect</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start gap-2">
                    <div className="w-1 h-1 bg-[#F97316] mt-2 flex-shrink-0"></div>
                    <span>Quick response (24-48 hours)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1 h-1 bg-[#F97316] mt-2 flex-shrink-0"></div>
                    <span>Creative consultation call</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1 h-1 bg-[#F97316] mt-2 flex-shrink-0"></div>
                    <span>Custom treatment & proposal</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1 h-1 bg-[#F97316] mt-2 flex-shrink-0"></div>
                    <span>Transparent pricing</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <form onSubmit={handleSubmit} className="bg-[#0A0A0A] border border-[#262626] p-8 rounded-none" data-testid="studio-contact-form">
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-white mb-2 block">Name *</Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="bg-[#030303] border-[#262626] text-white rounded-none"
                      data-testid="studio-contact-name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-white mb-2 block">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="bg-[#030303] border-[#262626] text-white rounded-none"
                      data-testid="studio-contact-email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="company" className="text-white mb-2 block">Company</Label>
                    <Input
                      id="company"
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="bg-[#030303] border-[#262626] text-white rounded-none"
                      data-testid="studio-contact-company"
                    />
                  </div>

                  <div>
                    <Label htmlFor="service_type" className="text-white mb-2 block">Project Type *</Label>
                    <select
                      id="service_type"
                      value={formData.service_type}
                      onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                      required
                      className="w-full px-3 py-2 bg-[#030303] border border-[#262626] text-white rounded-none"
                      data-testid="studio-contact-project-type"
                    >
                      <option value="production">Brand Film / Commercial</option>
                      <option value="social">Social Content</option>
                      <option value="motion">Motion Graphics</option>
                      <option value="photography">Photography</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-white mb-2 block">Project Details *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      rows={5}
                      className="bg-[#030303] border-[#262626] text-white rounded-none"
                      placeholder="Tell us about your vision, timeline, and budget..."
                      data-testid="studio-contact-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white py-6 rounded-none font-medium text-lg"
                    data-testid="studio-contact-submit"
                  >
                    {submitting ? (
                      "Sending..."
                    ) : (
                      <>
                        Send Message <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer brand="studio" />
    </div>
  );
};

export default StudioContact;